import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Timer, SignIn, SignOut, Trash, FloppyDisk } from "@phosphor-icons/react";
import { parseNum } from "@/lib/utils";

export const fmtBRL = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
export const fmtHours = (h) => { const m = Math.round((Number(h) || 0) * 60); return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`; };
export const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const PLACE = { carregamento: "Carregamento", descarga: "Descarga" };

function DetentionRow({ d, canManage, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [a, setA] = useState(toLocalInput(d.arrived_at));
  const [l, setL] = useState(toLocalInput(d.left_at));
  const save = async () => {
    try {
      await api.put(`/detentions/${d.id}`, { arrived_at: new Date(a).toISOString(), left_at: l ? new Date(l).toISOString() : null, place: d.place });
      toast.success("Horários corrigidos"); setEdit(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const del = async () => { if (!window.confirm("Excluir este registro de estadia?")) return; await api.delete(`/detentions/${d.id}`); onChanged(); };
  return (
    <div data-testid={`detention-row-${d.id}`} className="border rounded-lg p-3 text-sm space-y-1">
      <div className="flex justify-between gap-2">
        <span className="font-semibold">{PLACE[d.place] || d.place}{d.open && <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">contando</span>}</span>
        <span className="font-bold text-emerald-700">{fmtBRL(d.value)}</span>
      </div>
      {edit ? (
        <div className="grid grid-cols-1 gap-2">
          <div><Label className="text-xs">Chegada</Label><Input type="datetime-local" value={a} onChange={(e) => setA(e.target.value)} data-testid="detention-edit-arrived" /></div>
          <div><Label className="text-xs">Saída</Label><Input type="datetime-local" value={l} onChange={(e) => setL(e.target.value)} data-testid="detention-edit-left" /></div>
          <Button size="sm" onClick={save} data-testid="btn-detention-edit-save" className="bg-blue-600 text-white gap-1"><FloppyDisk size={14} /> Salvar horários</Button>
        </div>
      ) : (
        <div className="text-slate-600">Chegou {fmtDT(d.arrived_at)} · Saiu {fmtDT(d.left_at)} · <b>{fmtHours(d.hours)}</b> parado · cobrável {fmtHours(d.billable_hours)}</div>
      )}
      {canManage && !edit && (
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid={`btn-detention-edit-${d.id}`}>Corrigir horários</Button>
          <Button size="sm" variant="ghost" onClick={del} data-testid={`btn-detention-delete-${d.id}`}><Trash size={14} className="text-red-600" /></Button>
        </div>
      )}
    </div>
  );
}

export function DetentionDialog({ trip, onClose, onTripChanged }) {
  const { user } = useAuth();
  const canManage = user?.role === "company" || user?.role === "autonomo";
  const canClock = user?.role === "driver" || user?.role === "autonomo";
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState({ enabled: false, free_hours: "5", rate_hour: "" });
  const [place, setPlace] = useState("descarga");
  const [busy, setBusy] = useState(false);

  const load = () => trip && api.get(`/trips/${trip.id}/detentions`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    if (!trip) return;
    setCfg({ enabled: !!trip.detention_enabled, free_hours: String(trip.detention_free_hours ?? 5), rate_hour: trip.detention_rate_hour ? String(trip.detention_rate_hour).replace(".", ",") : "" });
    load();
    // eslint-disable-next-line
  }, [trip?.id]);

  const saveCfg = async () => {
    try {
      await api.put(`/trips/${trip.id}/detention-settings`, { enabled: cfg.enabled, free_hours: parseNum(cfg.free_hours), rate_hour: parseNum(cfg.rate_hour) });
      toast.success(cfg.enabled ? (user.role === "company" ? "Estadia ativada — motorista avisado" : "Controle de estadia ativado") : "Controle de estadia desligado");
      onTripChanged?.(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const clock = async (kind) => {
    setBusy(true);
    const send = async (pos) => {
      try {
        if (kind === "arrive") await api.post(`/trips/${trip.id}/detentions/arrive`, { place, lat: pos?.coords.latitude, lng: pos?.coords.longitude });
        else await api.post(`/trips/${trip.id}/detentions/leave`);
        toast.success(kind === "arrive" ? "Chegada registrada — estadia contando" : "Saída registrada");
        load();
      } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
    };
    if (kind === "arrive" && navigator.geolocation) navigator.geolocation.getCurrentPosition(send, () => send(null), { timeout: 8000 });
    else send(null);
  };

  const enabled = trip?.detention_enabled;
  const openRow = rows.find((r) => r.open);
  return (
    <Dialog open={!!trip} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="detention-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Timer size={22} weight="duotone" className="text-blue-600" /> Estadia — {trip?.client || "cliente"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {canManage && (
            <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="det-enabled" className="font-semibold">{user.role === "company" ? "Pedir ao motorista para marcar a estadia" : "Controlar estadia neste frete"}</Label>
                <Switch id="det-enabled" checked={cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="detention-enabled-switch" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Horas grátis</Label><Input inputMode="decimal" value={cfg.free_hours} onChange={(e) => setCfg((c) => ({ ...c, free_hours: e.target.value }))} data-testid="detention-free-hours" /></div>
                <div><Label className="text-xs">Valor por hora (R$)</Label><Input inputMode="decimal" placeholder="ex: 80,00" value={cfg.rate_hour} onChange={(e) => setCfg((c) => ({ ...c, rate_hour: e.target.value }))} data-testid="detention-rate" /></div>
              </div>
              <p className="text-xs text-slate-500">Lei 11.442/2007: as primeiras 5 horas são grátis; depois disso a estadia é cobrável.</p>
              <Button size="sm" onClick={saveCfg} data-testid="btn-detention-save-settings" className="bg-blue-600 hover:bg-blue-700 text-white w-full">Salvar configuração</Button>
            </div>
          )}
          {canClock && enabled && (
            <div className="space-y-2">
              {!openRow && (
                <Select value={place} onValueChange={setPlace}>
                  <SelectTrigger data-testid="detention-place"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="carregamento">No carregamento</SelectItem><SelectItem value="descarga">Na descarga</SelectItem></SelectContent>
                </Select>
              )}
              {openRow ? (
                <Button disabled={busy} onClick={() => clock("leave")} data-testid="btn-detention-leave" className="w-full h-14 text-base bg-red-600 hover:bg-red-700 text-white gap-2"><SignOut size={22} weight="bold" /> Saí do cliente</Button>
              ) : (
                <Button disabled={busy} onClick={() => clock("arrive")} data-testid="btn-detention-arrive" className="w-full h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white gap-2"><SignIn size={22} weight="bold" /> Cheguei no cliente</Button>
              )}
            </div>
          )}
          {canClock && !enabled && !canManage && <p className="text-sm text-slate-500">A empresa ainda não ativou o controle de estadia neste frete.</p>}
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-blue-700">Registros</div>
            {rows.length === 0 && <p className="text-sm text-slate-500">Nenhum registro ainda.</p>}
            {rows.map((d) => <DetentionRow key={d.id + d.arrived_at + d.left_at} d={d} canManage={canManage} onChanged={load} />)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
